import { useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { authApi } from '../../api/auth';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/auth';


import {
  clearReferralCode,
  readReferralCode,
  saveReferralCode,
} from '../../features/referral/storage';
import { errorText } from '../../i18n/errors';

export function RegisterPage() {
  const { t, i18n } = useTranslation();
  // Названия документов приходят с сервера на языке интерфейса: под галочкой
  // «принимаю» не должно стоять название на языке, которого человек не знает.
  const locale = i18n.resolvedLanguage ?? 'en';
  const [params] = useSearchParams();
  const setSession = useAuthStore((state) => state.setSession);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [accepted, setAccepted] = useState(false);

  /**
   * Код мог прийти на лендинг задолго до регистрации (§40.3), поэтому
   * запоминаем его локально, а не полагаемся на текущий URL.
   */
  useEffect(() => {
    const code = params.get('ref');
    if (code) saveReferralCode(code);
  }, [params]);

  const documents = useQuery({
    queryKey: ['consent-documents', locale],
    queryFn: () => authApi.consentDocuments(locale),
    staleTime: 5 * 60_000,
  });

  const register = useMutation({
    mutationFn: () => {
      const acceptedConsents = Object.fromEntries(
        (documents.data ?? []).map((document) => [document.type, document.version]),
      );
      const referralCode = readReferralCode();
      return authApi.register({
        email,
        password,
        ...(name.trim() ? { name: name.trim() } : {}),
        ...(referralCode ? { referralCode } : {}),
        // Язык уезжает сразу: письмо с подтверждением отправляется в тот же
        // миг, и без этого оно придёт на языке по умолчанию, а не на том,
        // на котором человек только что читал форму.
        locale,
        acceptedConsents,
      });
    },
    onSuccess: (session) => {
      clearReferralCode();
      // Куда вести дальше, решает RequireAnonymous: смена статуса на
      // authenticated срабатывает раньше любого navigate() отсюда.
      setSession(session.user);
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    register.mutate();
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-5">
      <h1 className="text-section-title font-semibold">{t('auth.register.title')}</h1>

      {register.isError ? (
        <Alert tone="error">{errorText(register.error)}</Alert>
      ) : null}

      <Input
        label={t('auth.field.name')}
        name="name"
        autoComplete="name"
        value={name}
        onChange={(event) => setName(event.target.value)}
      />
      <Input
        label={t('auth.field.email')}
        name="email"
        type="email"
        autoComplete="email"
        required
        value={email}
        onChange={(event) => setEmail(event.target.value)}
      />
      <Input
        label={t('auth.field.password')}
        name="password"
        type="password"
        autoComplete="new-password"
        required
        minLength={8}
        hint={t('auth.field.passwordHint')}
        value={password}
        onChange={(event) => setPassword(event.target.value)}
      />

      {/* §4: пять документов принимаются при регистрации, каждый — своей версии */}
      <label className="flex cursor-pointer items-start gap-3 text-meta text-content-secondary">
        <input
          type="checkbox"
          checked={accepted}
          onChange={(event) => setAccepted(event.target.checked)}
          className="mt-0.5 h-4 w-4 shrink-0 accent-accent"
          required
        />
        <span>
          {t('auth.register.accept')}{' '}
          {(documents.data ?? []).map((document, index, list) => (
            <span key={document.type}>
              <Link
                to={`/legal/${document.type}`}
                className="text-content underline decoration-line-strong underline-offset-2 hover:decoration-content"
              >
                {document.title.toLowerCase()}
              </Link>
              {index < list.length - 1 ? ', ' : ''}
            </span>
          ))}
          .
        </span>
      </label>

      <Button
        type="submit"
        variant="primary"
        disabled={register.isPending || !accepted || documents.data === undefined}
      >
        {register.isPending ? t('auth.register.pending') : t('auth.register.title')}
      </Button>

      <p className="text-center text-meta text-content-secondary">
        {t('auth.register.haveAccount')}{' '}
        <Link to="/login" className="text-accent hover:text-accent-hover">{t('auth.login.submit')}</Link>
      </p>
    </form>
  );
}
